/** Pre-filing validation (M07-33) and tax-vs-ledger reconciliation (M07-32). */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, TriangleAlert, XCircle } from 'lucide-react';
import { api } from '../../lib/api';
import { amount } from '../../lib/money';
import { manilaToday } from '../../lib/queries';
import { Card, FilterBar, Money, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, filterClass } from '../../components/ui';

interface Issue { severity: 'ERROR' | 'WARNING'; code: string; message: string; link?: string }
interface Rec { key: string; label: string; ledger: string; documents: string; difference: string; ties: boolean }

export function TaxChecksPage(): JSX.Element {
  const today = manilaToday();
  const [year, setYear] = useState(Number(today.slice(0, 4)));
  const [quarter, setQuarter] = useState(Math.floor((Number(today.slice(5, 7)) - 1) / 3) + 1);
  const v = useQuery({ queryKey: ['tax-validation', year, quarter], queryFn: () => api<{ issues: Issue[]; ok: boolean }>(`/tax-returns/validation?year=${year}&quarter=${quarter}`) });
  const rec = useQuery({ queryKey: ['tax-rec', year, quarter], queryFn: () => api<Rec[]>(`/tax-returns/reconciliation?year=${year}&quarter=${quarter}`) });
  return (
    <>
      <PageHeader title="Pre-filing Checks" description="Fix these before generating the quarter's returns: missing TINs, invalid ATCs, unposted drafts, 2307 gaps, and any tax account that doesn't tie to its documents." />
      <FilterBar>
        <select className={filterClass} value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label="Year">{[0, 1, 2].map((d) => Number(today.slice(0, 4)) - d).map((y) => <option key={y} value={y}>{y}</option>)}</select>
        <select className={filterClass} value={quarter} onChange={(e) => setQuarter(Number(e.target.value))} aria-label="Quarter">{[1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q}</option>)}</select>
      </FilterBar>
      <div className="grid gap-4 xl:grid-cols-2">
        <Card title="Validation" actions={v.data && <StatusChip tone={v.data.ok ? 'ok' : 'critical'}>{v.data.ok ? 'Ready to file' : 'Needs fixing'}</StatusChip>}>
          {v.isLoading && <Skeleton className="h-32 w-full" />}
          {v.data?.issues.length === 0 && <p className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400"><CheckCircle2 aria-hidden="true" className="h-4 w-4" /> No issues found.</p>}
          <ul className="space-y-2">
            {v.data?.issues.map((i, n) => (
              <li key={n} className="flex items-start gap-2 text-sm">
                {i.severity === 'ERROR' ? <XCircle aria-label="Error" className="mt-0.5 h-4 w-4 shrink-0 text-red-600" /> : <TriangleAlert aria-label="Warning" className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />}
                <span>{i.message} {i.link && <Link className="text-brand-700 underline dark:text-brand-100" to={i.link}>Fix</Link>}</span>
              </li>
            ))}
          </ul>
        </Card>
        <Card title="Tax reconciliation" description="Tax per ledger vs tax per documents for the quarter.">
          {rec.isLoading && <Skeleton className="h-32 w-full" />}
          {rec.data && (
            <TableWrap>
              <thead><tr className="border-b border-slate-200 dark:border-slate-800"><Th>Tax</Th><Th align="right">Ledger</Th><Th align="right">Documents</Th><Th align="right">Difference</Th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {rec.data.map((r) => (
                  <tr key={r.key}>
                    <Td>{r.label}</Td>
                    <Td align="right"><Money>{amount(r.ledger)}</Money></Td>
                    <Td align="right"><Money>{amount(r.documents)}</Money></Td>
                    <Td align="right"><StatusChip tone={r.ties ? 'ok' : 'critical'}>{amount(r.difference)}</StatusChip></Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Card>
      </div>
    </>
  );
}
